import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Skeleton } from "../ui/skeleton";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { UserPlus, Upload, Trash2, Pencil, ShieldCheck, Users2, ShieldPlus, ShieldOff, X, Repeat2, CalendarPlus, KeyRound } from "lucide-react";
import AssignSaturdaysDialog from "./AssignSaturdaysDialog";

function GroupBadge({ role, group }) {
  if (role === "admin") {
    return <span className="px-2 py-0.5 rounded-full bg-brand-blue text-white text-[10px] uppercase tracking-wider font-bold">Admin</span>;
  }
  if (group === "board") {
    return <span className="px-2 py-0.5 rounded-full bg-brand-blue/10 text-brand-blue text-[10px] uppercase tracking-wider font-bold">Board</span>;
  }
  if (group === "alternate") {
    return <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] uppercase tracking-wider font-bold">Alternate</span>;
  }
  return <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-[10px] uppercase tracking-wider font-bold">—</span>;
}

export default function MembersTab() {
  const [members, setMembers] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", member_group: "board", password: "" });
  const [selected, setSelected] = useState(new Set());
  const [cascadeSignups, setCascadeSignups] = useState(true);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [assigning, setAssigning] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/members"); setMembers(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: "", email: "", phone: "", member_group: "board", password: "" }); setEditing("__new__"); };
  const openEdit = (m) => { setForm({ name: m.name, email: m.email, phone: m.phone || "", member_group: m.member_group || "board", password: "" }); setEditing(m.id); };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required.");
    try {
      if (editing === "__new__") {
        const { data } = await api.post("/admin/members", form);
        toast.success(`Added ${data.member.name}. Temp password: ${data.temp_password}`);
      } else {
        const { name, phone, member_group, password } = form;
        const body = { name, phone, member_group };
        if (password) body.password = password;
        await api.put(`/admin/members/${editing}`, body);
        toast.success("Member updated.");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the member directory?`)) return;
    try { await api.delete(`/admin/members/${m.id}`); toast.success("Removed."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filtered = (members || []).filter((m) => {
    if (filter === "admin") {
      if (m.role !== "admin") return false;
    } else if (filter !== "all" && m.member_group !== filter) {
      return false;
    }
    const s = search.trim().toLowerCase();
    if (!s) return true;
    return [m.name, m.email, m.phone].some((v) => (v || "").toLowerCase().includes(s));
  });

  const counts = (members || []).reduce((acc, m) => {
    if (m.role === "admin") acc.admin = (acc.admin || 0) + 1;
    else acc[m.member_group || "other"] = (acc[m.member_group || "other"] || 0) + 1;
    return acc;
  }, {});

  const toggleAdmin = async (m) => {
    const target = m.role === "admin" ? "driver" : "admin";
    const word = target === "admin" ? "Promote" : "Demote";
    if (!window.confirm(`${word} ${m.name} ${target === "admin" ? "to super-admin?" : "to regular volunteer?"}`)) return;
    try {
      await api.put(`/admin/members/${m.id}`, { role: target });
      toast.success(`${m.name} is now ${target === "admin" ? "an admin" : "a volunteer"}.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const sendResetLink = async (m) => {
    if (!window.confirm(`Email a password-reset link to ${m.name} (${m.email})?`)) return;
    try {
      await api.post(`/admin/members/${m.id}/send-password-reset`);
      toast.success(`Reset link sent to ${m.email}. It's good for 60 minutes.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send reset link.");
    }
  };

  // ---- Bulk selection helpers ----
  const filteredIds = useMemo(() => filtered.map((m) => m.id), [filtered]);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const someFilteredSelected = filteredIds.some((id) => selected.has(id));

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredIds.forEach((id) => next.delete(id));
      } else {
        filteredIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };
  const invertFilteredSelection = () => {
    // "Keep these, delete the rest" — select every filtered row NOT currently selected.
    setSelected((prev) => {
      const next = new Set();
      filteredIds.forEach((id) => { if (!prev.has(id)) next.add(id); });
      return next;
    });
    toast.info("Selection inverted — the rows you had checked are now the ones you're keeping.");
  };
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/admin/members/bulk-delete", {
        ids: Array.from(selected),
        cascade_signups: cascadeSignups,
      });
      let msg = `Removed ${data.deleted_count} member${data.deleted_count === 1 ? "" : "s"}.`;
      if (data.cascaded_signups) msg += ` Cleared ${data.cascaded_signups} upcoming signup${data.cascaded_signups === 1 ? "" : "s"}.`;
      if (data.skipped_self) msg += " Your own account was skipped.";
      toast.success(msg);
      clearSelection();
      setConfirmBulkDelete(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBulkDeleting(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-semibold text-slate-900">Member directory</h2>
          <p className="text-sm text-slate-500">
            Admins: <strong className="text-slate-700">{counts.admin || 0}</strong> ·
            Board: <strong className="text-slate-700">{counts.board || 0}</strong> ·
            Alternates: <strong className="text-slate-700">{counts.alternate || 0}</strong>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="members-import-button" variant="outline" onClick={() => setImporting(true)} className="border-slate-300 hover:border-brand-blue hover:text-brand-blue">
            <Upload className="w-4 h-4 mr-1.5" /> Import CSV
          </Button>
          <Button data-testid="members-new-button" onClick={openNew} className="bg-brand-blue hover:bg-brand-blue-dark">
            <UserPlus className="w-4 h-4 mr-1.5" /> Add member
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <Input
          data-testid="members-search"
          placeholder="Search name, email, or phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <div className="flex gap-1">
          {[
            { v: "all", label: "All" },
            { v: "admin", label: "Admins" },
            { v: "board", label: "Board" },
            { v: "alternate", label: "Alternates" },
          ].map((b) => (
            <button
              key={b.v}
              data-testid={`members-filter-${b.v}`}
              onClick={() => setFilter(b.v)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                filter === b.v ? "bg-brand-blue text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      {!members && <Skeleton className="h-48 rounded-xl" />}

      {members && filtered.length === 0 && (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-xl">
          <Users2 className="w-10 h-10 mx-auto text-slate-300" />
          <p className="text-slate-600 mt-2">No members match these filters.</p>
        </div>
      )}

      {members && filtered.length > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              data-testid="members-select-all"
              checked={allFilteredSelected ? true : (someFilteredSelected ? "indeterminate" : false)}
              onCheckedChange={toggleAllFiltered}
            />
            <span className="font-semibold text-slate-700">
              Select all {filter === "all" ? "" : `filtered `}({filtered.length})
            </span>
          </label>
          <div className="flex items-center gap-3 text-slate-500">
            {selected.size > 0 && (
              <button
                data-testid="members-invert-selection"
                onClick={invertFilteredSelection}
                className="flex items-center gap-1 font-semibold text-slate-600 hover:text-brand-blue"
              >
                <Repeat2 className="w-3.5 h-3.5" /> Invert (keep checked)
              </button>
            )}
            <span className="font-bold uppercase tracking-wider">
              {selected.size} selected
            </span>
          </div>
        </div>
      )}

      <div className={`bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 ${selected.size > 0 ? "pb-20" : ""}`}>
        {filtered.map((m) => (
          <div
            key={m.id}
            data-testid={`member-row-${m.id}`}
            className={`flex flex-wrap items-center gap-3 p-4 transition-colors ${selected.has(m.id) ? "bg-brand-blue/5" : ""}`}
          >
            <Checkbox
              data-testid={`member-select-${m.id}`}
              checked={selected.has(m.id)}
              onCheckedChange={() => toggleRow(m.id)}
              aria-label={`Select ${m.name}`}
            />
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-900">{m.name}</span>
                <GroupBadge role={m.role} group={m.member_group} />
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{m.email}{m.phone ? ` · ${m.phone}` : ""}</div>
            </div>
            <div className="flex gap-2 ml-auto flex-wrap">
              <Button
                size="sm"
                variant="ghost"
                data-testid={`member-assign-${m.id}`}
                onClick={() => setAssigning(m)}
                className="text-brand-blue hover:text-brand-blue-dark"
              >
                <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Assign
              </Button>
              <Button
                size="sm"
                variant="ghost"
                data-testid={`member-toggle-admin-${m.id}`}
                onClick={() => toggleAdmin(m)}
                className={m.role === "admin" ? "text-amber-700 hover:text-amber-800" : "text-brand-blue hover:text-brand-blue-dark"}
              >
                {m.role === "admin"
                  ? <><ShieldOff className="w-3.5 h-3.5 mr-1" /> Demote</>
                  : <><ShieldPlus className="w-3.5 h-3.5 mr-1" /> Promote</>}
              </Button>
              <Button size="sm" variant="ghost" data-testid={`member-edit-${m.id}`} onClick={() => openEdit(m)} className="text-slate-600 hover:text-brand-blue">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
              </Button>
              <Button size="sm" variant="ghost" data-testid={`member-send-reset-${m.id}`} onClick={() => sendResetLink(m)} className="text-slate-600 hover:text-brand-blue">
                <KeyRound className="w-3.5 h-3.5 mr-1" /> Send reset
              </Button>
              <Button size="sm" variant="ghost" data-testid={`member-delete-${m.id}`} onClick={() => del(m)} className="text-slate-500 hover:text-rose-600">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="member-edit-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing === "__new__" ? "Add member" : "Edit member"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-semibold">Name</Label>
              <Input data-testid="member-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Email</Label>
              <Input data-testid="member-email-input" type="email" value={form.email} disabled={editing !== "__new__"} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
              {editing !== "__new__" && <p className="text-xs text-slate-400 mt-1">Email can&apos;t be changed.</p>}
            </div>
            <div>
              <Label className="text-sm font-semibold">Phone</Label>
              <Input data-testid="member-phone-input" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Group</Label>
              <Select value={form.member_group} onValueChange={(v) => setForm({ ...form, member_group: v })}>
                <SelectTrigger data-testid="member-group-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="board">Board Member</SelectItem>
                  <SelectItem value="alternate">Alternate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold">{editing === "__new__" ? "Initial password (optional)" : "Reset password (optional)"}</Label>
              <Input data-testid="member-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing === "__new__" ? "Leave blank to use default" : "Leave blank to keep current"} className="mt-1.5 font-mono" />
              <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><ShieldCheck className="w-3 h-3" />Default password comes from Settings.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="member-save-button" onClick={save} className="bg-brand-blue hover:bg-brand-blue-dark">
              {editing === "__new__" ? "Add member" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportDialog open={importing} onClose={() => setImporting(false)} onDone={load} />
      <AssignSaturdaysDialog
        member={assigning}
        open={!!assigning}
        onClose={() => setAssigning(null)}
        onDone={load}
      />

      {selected.size > 0 && (
        <div
          data-testid="members-bulk-bar"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-xl w-[calc(100%-2rem)] rounded-full shadow-lg bg-slate-900 text-white px-4 py-3 flex items-center gap-3 border border-slate-700"
        >
          <span className="flex items-center gap-2 text-sm">
            <ShieldCheck className="w-4 h-4 text-brand-blue" />
            <span className="font-bold">{selected.size}</span>
            <span className="opacity-80">selected</span>
          </span>
          <div className="flex-1" />
          <button
            data-testid="members-bulk-clear"
            onClick={clearSelection}
            className="text-xs font-semibold opacity-80 hover:opacity-100 flex items-center gap-1"
          >
            <X className="w-3.5 h-3.5" /> Clear
          </button>
          <Button
            data-testid="members-bulk-delete"
            onClick={() => setConfirmBulkDelete(true)}
            size="sm"
            className="bg-rose-600 hover:bg-rose-700 text-white font-semibold min-h-[36px]"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove {selected.size}
          </Button>
        </div>
      )}

      <AlertDialog open={confirmBulkDelete} onOpenChange={setConfirmBulkDelete}>
        <AlertDialogContent data-testid="members-bulk-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              Remove {selected.size} member{selected.size === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              These members will lose access to the app immediately. Your own account is skipped automatically. Admins in the selection are only removed if at least one admin remains.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 cursor-pointer">
            <Checkbox
              data-testid="members-cascade-signups"
              checked={cascadeSignups}
              onCheckedChange={(v) => setCascadeSignups(!!v)}
              className="mt-0.5"
            />
            <div className="text-sm text-slate-700">
              <p className="font-semibold">Also delete their upcoming signups</p>
              <p className="text-xs text-slate-500 mt-0.5">Historical (past) signups are always preserved for records. Recommended for test-account cleanup.</p>
            </div>
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="members-bulk-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="members-bulk-confirm"
              onClick={bulkDelete}
              disabled={bulkDeleting}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {bulkDeleting ? "Removing…" : `Remove ${selected.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ImportDialog({ open, onClose, onDone }) {
  const [csv, setCsv] = useState("");
  const [defaultGroup, setDefaultGroup] = useState("board");
  const [defaultPw, setDefaultPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const rows = [];
    const errors = [];
    csv.split(/\r?\n/).forEach((line, i) => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.toLowerCase().startsWith("name,") || trimmed.startsWith("#")) return;
      const parts = trimmed.split(",").map((p) => p.trim());
      const [name, email, phone = "", group = ""] = parts;
      if (!name || !email) { errors.push(`Line ${i + 1}: missing name or email`); return; }
      let g = (group || defaultGroup).toLowerCase();
      if (!["board", "alternate"].includes(g)) g = defaultGroup;
      rows.push({ name, email, phone, member_group: g });
    });
    if (errors.length) toast.warning(errors.slice(0, 3).join(" · "));
    if (!rows.length) return toast.error("No valid rows to import.");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/members/import", { rows, default_password: defaultPw || undefined });
      if (data.errors?.length) {
        toast.warning(`${data.created} added · ${data.updated} updated · ${data.skipped} skipped. First skip: ${data.errors[0]}`);
      } else {
        toast.success(`Imported ${data.created} new · updated ${data.updated} · skipped ${data.skipped}. Default password: ${data.default_password}`);
      }
      onDone();
      onClose();
      setCsv("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="members-import-dialog" className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Bulk import members</DialogTitle>
          <DialogDescription>
            Paste one member per line: <span className="font-mono text-slate-700">Name, Email, Phone, Group</span> (Phone & Group optional).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 font-mono leading-relaxed">
            Jennifer McMahon, jnmcmahon169@example.com, 555-123-4567, board<br />
            Anthony Cocolas, acocolas@example.com, 555-987-6543, alternate
          </div>
          <Textarea
            data-testid="members-import-textarea"
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            rows={10}
            className="font-mono text-sm"
            placeholder="Name, Email, Phone, Group"
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-semibold">Default group (if not in row)</Label>
              <Select value={defaultGroup} onValueChange={setDefaultGroup}>
                <SelectTrigger data-testid="import-default-group" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="board">Board Member</SelectItem>
                  <SelectItem value="alternate">Alternate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold">Default password (optional)</Label>
              <Input data-testid="import-default-password" value={defaultPw} onChange={(e) => setDefaultPw(e.target.value)} placeholder="Use Settings default" className="mt-1.5 font-mono" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="members-import-submit" onClick={submit} disabled={busy} className="bg-brand-blue hover:bg-brand-blue-dark">
            {busy ? "Importing…" : "Import members"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
